import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ToolResult } from "@/hooks/useAgentChat";
import { getEntityRoute, getToolDisplayConfig, getNextStep } from "@/lib/chat-utils";

interface ActionFeedbackCardProps {
  toolResult: ToolResult;
  onNextAction?: (action: string) => void;
}

const isNonEmpty = (v: unknown): v is string => typeof v === "string" && v.length > 0;

/** Inspection number as a "#NN" chip, or undefined when absent. */
function inspectionNumberChip(inspection: Record<string, unknown>): string | undefined {
  const n = inspection.inspection_number;
  if (typeof n === "number") return `#${n}`;
  if (isNonEmpty(n)) return `#${n}`;
  return undefined;
}

/** Collect human-readable entity detail chips from a write tool result. */
function extractEntityDetails(result: Record<string, unknown>): string[] {
  const data = (result.data ?? {}) as Record<string, unknown>;
  const inspection = (data.inspection ?? {}) as Record<string, unknown>;
  const defect = (data.defect ?? {}) as Record<string, unknown>;

  const candidates: unknown[] = [
    inspectionNumberChip(inspection),
    inspection.checklist_name,
    defect.title,
    defect.severity,
  ];
  return candidates.filter(isNonEmpty);
}

/**
 * Renders a rich action feedback card for write tool results.
 * Shows entity link, success/failed badge, and next-step button.
 * Used to replace ToolBadge pills for write operations (D-04, D-05).
 */
export function ActionFeedbackCard({ toolResult, onNextAction }: ActionFeedbackCardProps) {
  const { t } = useTranslation();
  const { tool, success, result } = toolResult;

  const config = getToolDisplayConfig(tool);
  const entityRoute = getEntityRoute(tool, result);
  const nextStep = getNextStep(tool, result);

  const Icon = config.icon;
  const entityDetails = extractEntityDetails(result);
  const hasFooter = entityRoute !== null || nextStep !== null;

  return (
    <Card
      className={cn(
        "border-l-4 animate-in fade-in slide-in-from-bottom-1 duration-300",
        config.borderColor,
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="text-base font-semibold leading-tight">{t(config.i18nKey)}</span>
          </div>
          <Badge
            className={cn(
              "shrink-0 text-xs font-normal",
              success ? config.badgeColor : "bg-destructive/10 text-destructive",
            )}
            variant="secondary"
          >
            {success ? t(config.i18nKey) : t("inspectionChat.tools.failed")}
          </Badge>
        </div>

        {entityDetails.length > 0 && (
          <p className="mt-2 text-sm text-muted-foreground truncate">{entityDetails.join(" · ")}</p>
        )}
      </CardContent>

      {hasFooter && (
        <CardFooter className="px-4 pb-4 pt-0 flex flex-wrap gap-2">
          {entityRoute && (
            <Button variant="ghost" size="sm" asChild>
              <Link
                to={entityRoute}
                aria-label={`${t(config.i18nKey)} ${t("inspectionChat.actions.viewEntity")}`}
              >
                {t("inspectionChat.actions.viewEntity")}
                <ExternalLink className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          )}
          {nextStep && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onNextAction?.(
                  t(nextStep.actionI18nKey, nextStep.actionParams || {}) || nextStep.action,
                )
              }
            >
              {t(nextStep.i18nKey)}
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
